/**
 * A length said in days and in hours, and read back either way.
 *
 * The plan is drawn a day at a time — a bar starts on a date and ends on one — so the
 * stored length is whole working days. Saying it only in days is no use to anyone who
 * bills by the hour, so every place that shows a length shows both, converted through
 * the project's working day (eight hours unless the project says otherwise), and every
 * place that takes one accepts either: `3d`, `24h`, `1.5d`, or a bare number of days.
 *
 * Hours round up to the day. Twelve hours of an eight-hour day is a two-day bar, not a
 * day and a half of one — there is no half a day to draw, and rounding down would
 * promise a day that was never in the plan.
 */

import { createContext, useContext } from 'react'

/** What a working day is worth when nothing has said. Matches `effort.DEFAULT_HOURS`. */
export const DEFAULT_DAY_HOURS = 8

export function dayHours(hours: number | null | undefined): number {
  return hours && hours > 0 ? hours : DEFAULT_DAY_HOURS
}

/** Trailing zeroes off a number, so eight hours reads "8h" and not "8.0h". */
function tidy(n: number): string {
  return Number(n.toFixed(2)).toString()
}

/** "3d" — the length in days alone, for a column too narrow for both. */
export function formatDays(days: number | null | undefined): string {
  return days === null || days === undefined ? '' : `${tidy(days)}d`
}

/** "3d · 24h" — the same length twice, for anywhere with room to say it. */
export function formatLength(days: number | null | undefined, perDay: number | null | undefined): string {
  if (days === null || days === undefined) return ''
  return `${tidy(days)}d · ${tidy(days * dayHours(perDay))}h`
}

/** "24h" — the length in hours alone. */
export function formatHours(days: number | null | undefined, perDay: number | null | undefined): string {
  return days === null || days === undefined ? '' : `${tidy(days * dayHours(perDay))}h`
}

export type Length = { duration_days: number } | { duration_hours: number }

/**
 * What somebody typed into a length field, as something the API takes.
 *
 * `null` for anything that is not a length — an empty box, a stray word, a negative —
 * so a caller can leave the row alone rather than write a nonsense number to it.
 */
export function parseLength(text: string): Length | null {
  const raw = text.trim().toLowerCase().replace(',', '.')
  if (!raw) return null
  const match = /^(\d+(?:\.\d+)?)\s*(d|day|days|h|hr|hrs|hour|hours|w|wk|week|weeks)?$/.exec(raw)
  if (!match) return null
  const value = Number(match[1])
  if (!Number.isFinite(value) || value <= 0) return null
  const unit = match[2] ?? 'd'
  if (unit.startsWith('h')) return { duration_hours: value }
  if (unit.startsWith('w')) return { duration_days: Math.max(1, Math.round(value * 5)) }
  return { duration_days: Math.max(1, Math.round(value)) }
}

/** The sentence under a length field, so nobody has to guess what the box takes. */
export function lengthHint(perDay: number | null | undefined): string {
  const h = tidy(dayHours(perDay))
  return `Working days. Type 3d or 24h — a day is ${h}h here, and hours round up to the day.`
}

/**
 * The project's working day, for anything deep enough in a screen that threading it
 * would mean touching every component between. Each screen that shows a length puts the
 * plan's figure in at the top; anything that reads it without a provider gets eight,
 * which is the same answer the API would give.
 */
const DayHoursContext = createContext<number>(DEFAULT_DAY_HOURS)
export const DayHoursProvider = DayHoursContext.Provider

export function useDayHours(): number {
  return useContext(DayHoursContext)
}
